import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Loading from '@/components/common/Loading'
import Badge from '@/components/common/Badge'
import ThisWeekCard from '@/components/portal/ThisWeekCard'

const FILE_SIGNED_URL_EXPIRY = 60 * 60 // 1 hour

const attachSignedUrls = async (fileList = []) => {
  if (!Array.isArray(fileList) || fileList.length === 0) return []

  const mappedFiles = await Promise.all(
    fileList.map(async (file) => {
      if (!file?.file_path || !file?.is_location_sensitive) {
        return { ...file, signedUrl: null }
      }

      try {
        const { data, error } = await supabase.storage
          .from('team-files')
          .createSignedUrl(file.file_path, FILE_SIGNED_URL_EXPIRY)

        if (error || !data?.signedUrl) {
          console.error('Failed to generate signed URL for file', file.id, error)
          return { ...file, signedUrl: null }
        }

        return { ...file, signedUrl: data.signedUrl }
      } catch (err) {
        console.error('Error generating signed URL for file', file.id, err)
        return { ...file, signedUrl: null }
      }
    })
  )

  return mappedFiles
}

function Portal() {
  const { slug } = useParams()
  const [portalData, setPortalData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [contactForms, setContactForms] = useState({})

  useEffect(() => {
    fetchPortalData()
  }, [slug])

  const fetchPortalData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const { data, error: rpcError } = await supabase
        .rpc('get_portal_data_v2', { slug_param: slug })

      if (rpcError) throw rpcError

      if (!data) {
        setError('Team not found')
        return
      }

      const filesWithUrls = await attachSignedUrls(data.files || [])

      // The RPC returns a JSONB object directly
      setPortalData({
        ...data,
        files: filesWithUrls
      })
    } catch (err) {
      console.error('Error fetching portal data:', err)
      setError(err.message || 'Failed to load team portal')
    } finally {
      setLoading(false)
    }
  }

  // Client-side search filter
  const filterContent = (items, query) => {
    if (!query || !items) return items || []
    const lowerQuery = query.toLowerCase()
    return items.filter(item => {
      const searchableText = JSON.stringify(item).toLowerCase()
      return searchableText.includes(lowerQuery)
    })
  }

  if (loading) {
    return <Loading text="Loading team portal..." />
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center py-12">
        <Container>
          <Card className="max-w-md mx-auto text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Team Not Found</h1>
            <p className="text-brand-slate mb-6">
              This team portal doesn't exist or the link may be incorrect.
            </p>
            <a href="/" className="text-brand-green hover:underline">
              Go to Bench Bulletin Home →
            </a>
          </Card>
        </Container>
      </div>
    )
  }

  const {
    team = {},
    dispatches = [],
    wiki_items = [],
    calendar_events = [],
    roster = [],
    snack_schedule = [],
    files = [],
    portal_links = [],
    portal_gallery_items = [],
    portal_video_items = [],
    portal_attachment_items = [],
    portal_social_links = [],
    roster_public,
    snacks_public,
    files_public,
    portal_sections = []
  } = portalData || {}
  
  const team_name = team.name || ''

  // Apply search filter
  const filteredDispatches = filterContent(dispatches, searchQuery)
  const filteredWikiItems = filterContent(wiki_items, searchQuery)
  const filteredEvents = filterContent(calendar_events, searchQuery)

  // Filter to only visible sections and sort by sort_order
  const visibleSections = portal_sections
    .filter(s => s.visible)
    .sort((a, b) => a.sort_order - b.sort_order)

  // Group sections by layout type
  const mainSections = visibleSections.filter(s => s.config?.layout !== 'sidebar')
  const sidebarSections = visibleSections.filter(s => s.config?.layout === 'sidebar')

  // Render section by type
  const renderSection = (section) => {
    switch (section.section_type) {
      case 'dispatches':
        return renderDispatchesSection(section)
      case 'wiki':
        return renderWikiSection(section)
      case 'calendar':
        return renderCalendarSection(section)
      case 'custom':
        return renderCustomSection(section)
      case 'links':
        return renderLinksSection(section)
      case 'gallery':
        return renderGallerySection(section)
      case 'videos':
        return renderVideosSection(section)
      case 'attachments':
        return renderAttachmentsSection(section)
      case 'contact_form':
        return renderContactFormSection(section)
      case 'event_calendar':
        return renderEventCalendarSection(section)
      case 'social_feed':
        return renderSocialFeedSection(section)
      case 'roster':
        return roster_public && roster.length > 0 ? renderRosterSection(section) : null
      case 'files':
        // Check if there are any public files (is_location_sensitive = true)
        const hasPublicFiles = files_public && files.some(f => f.is_location_sensitive)
        return hasPublicFiles ? renderFilesSection(section) : null
      case 'snacks':
        return snacks_public && snack_schedule.length > 0 ? renderSnacksSection(section) : null
      default:
        return null
    }
  }

  const renderDispatchesSection = (section) => {
    if (filteredDispatches.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-8">
            {searchQuery ? 'No dispatches match your search.' : 'No dispatches yet.'}
          </p>
        </Card>
      )
    }
    const showCount = 2
    const recent = filteredDispatches.slice(0, showCount)
    const older = filteredDispatches.slice(showCount)
    return (
      <div className="space-y-3 md:space-y-4">
        {recent.map((dispatch) => (
          <Card key={dispatch.id}>
            <div className="mb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-2">
                <h3 className="font-heading text-base md:text-lg font-bold break-words">
                  {dispatch.title}
                </h3>
                <span className="text-xs md:text-sm text-brand-slate whitespace-nowrap">
                  {new Date(dispatch.sent_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
            <div 
              className="prose prose-sm max-w-none text-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: dispatch.content }}
            />
          </Card>
        ))}
        {older.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-brand-green text-sm md:text-base px-2">Show older updates</summary>
            <div className="space-y-3 md:space-y-4 mt-3">
              {older.map((dispatch) => (
                <Card key={dispatch.id}>
                  <div className="mb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <h3 className="font-heading text-sm md:text-base font-semibold break-words">
                        {dispatch.title}
                      </h3>
                      <span className="text-xs text-brand-slate whitespace-nowrap">
                        {new Date(dispatch.sent_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none text-sm">
                    <div className="line-clamp-6" dangerouslySetInnerHTML={{ __html: dispatch.content }} />
                  </div>
                </Card>
              ))}
            </div>
          </details>
        )}
      </div>
    )
  }

  const renderWikiSection = (section) => {
    if (filteredWikiItems.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-4 text-sm">
            {searchQuery ? 'No wiki items match your search.' : 'No quick info yet.'}
          </p>
        </Card>
      )
    }

    // Helper to check if item was recently added (last 3 days)
    const isRecentlyAdded = (item) => {
      if (!item.created_at) return false
      const created = new Date(item.created_at)
      const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
      return created > threeDaysAgo
    }

    // Group items by category
    const grouped = filteredWikiItems.reduce((acc, item) => {
      const category = item.category || 'Quick Notes'
      if (!acc[category]) acc[category] = []
      acc[category].push(item)
      return acc
    }, {})
    // Sort deterministically: Pinned → sort_order → newest first → alphabetical (as final tiebreaker)
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => {
        // 1. Pinned items first
        if (a.is_pinned && !b.is_pinned) return -1
        if (!a.is_pinned && b.is_pinned) return 1
        // 2. Lower sort_order comes first (0 = top)
        const sortOrderA = a.sort_order ?? 999
        const sortOrderB = b.sort_order ?? 999
        if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB
        // 3. Most recently created first
        const dateA = new Date(a.created_at || 0).getTime()
        const dateB = new Date(b.created_at || 0).getTime()
        if (dateA !== dateB) return dateB - dateA // DESC
        // 4. Alphabetical by title as final tiebreaker
        return (a.title || '').localeCompare(b.title || '')
      })
    })
    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category}>
            {Object.keys(grouped).length > 1 && (
              <h3 className="font-medium text-sm text-brand-slate mb-2">
                {category}
              </h3>
            )}
            <div className="space-y-2">
              {items.map((item) => (
                <Card key={item.id} className={item.is_pinned ? 'border-2 border-amber-300' : ''}>
                  <div className="flex items-start gap-2 mb-2">
                    {item.is_pinned && (
                      <Badge variant="warning">Pinned</Badge>
                    )}
                    {isRecentlyAdded(item) && !item.is_pinned && (
                      <Badge variant="success" className="text-xs">New</Badge>
                    )}
                  </div>
                  <h4 className="font-medium text-brand-charcoal mb-1">
                    {item.title}
                  </h4>
                  <div 
                    className="text-sm text-brand-slate prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                  {item.source === 'ai_extracted' && (
                    <p className="text-xs text-brand-slate mt-2">
                      Auto-extracted
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderCalendarSection = (section) => {
    if (filteredEvents.length === 0) return null
    const limit = section.config?.limit || 10
    return (
      <div className="space-y-2 md:space-y-3">
        {filteredEvents.slice(0, limit).map((event) => (
          <Card key={event.id}>
            <div className="flex items-start gap-2 md:gap-3">
              <div className="flex-shrink-0 text-center min-w-[50px]">
                <div className="text-xl md:text-2xl font-bold text-brand-charcoal leading-tight">
                  {new Date(event.start_time).toLocaleDateString('en-US', { day: 'numeric' })}
                </div>
                <div className="text-xs text-brand-slate uppercase">
                  {new Date(event.start_time).toLocaleDateString('en-US', { month: 'short' })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm md:text-base text-brand-charcoal mb-1 break-words">
                  {event.title}
                </h4>
                <p className="text-xs md:text-sm text-brand-slate">
                  {new Date(event.start_time).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </p>
                {event.location && (
                  <p className="text-xs text-brand-slate mt-1 break-words">
                    📍 {event.location}
                  </p>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  const renderCustomSection = (section) => {
    if (!section.config?.custom_html) return null
    return (
      <Card>
        <div 
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: section.config.custom_html }}
        />
      </Card>
    )
  }

  const renderRosterSection = (section) => {
    return (
      <Card>
        <div className="space-y-2">
          {roster.map((player) => (
            <div key={player.id} className="flex items-center justify-between p-2 bg-brand-cream rounded">
              <span className="font-medium text-brand-charcoal">
                #{player.jersey_number} {player.first_name} {player.last_name?.charAt(0)}.
              </span>
              <span className="text-sm text-brand-slate">{player.position}</span>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  const renderFilesSection = (section) => {
    // Filter for public files that have valid signed URLs
    const publicFiles = files.filter((file) => file.is_location_sensitive && file.signedUrl)
    
    // Don't render section if no public files with valid URLs
    if (publicFiles.length === 0) return null

    return (
      <Card>
        <div className="space-y-2 md:space-y-3">
          {publicFiles.map((file) => (
            <div key={file.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 md:p-3 bg-brand-cream rounded-lg">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-brand-slate flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="min-w-0">
                  <p className="font-medium text-sm md:text-base text-brand-charcoal truncate">{file.name}</p>
                  <p className="text-xs text-brand-slate">
                    {formatFileSize(file.file_size)} • {new Date(file.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <a
                href={file.signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-green hover:underline text-xs md:text-sm whitespace-nowrap"
              >
                Download →
              </a>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  const renderSnacksSection = (section) => {
    return (
      <Card>
        <div className="space-y-2">
          {snack_schedule.map((snack, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-brand-cream rounded">
              <span className="text-sm text-brand-charcoal">
                {new Date(snack.game_date).toLocaleDateString()}
              </span>
              <span className="font-medium text-brand-charcoal">{snack.family_name}</span>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  const renderLinksSection = (section) => {
    const links = portal_links
      .filter(link => link.section_id === section.id && link.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

    if (links.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-4 text-sm">
            No links available yet.
          </p>
        </Card>
      )
    }

    return (
      <Card>
        <div className="space-y-3 md:space-y-4">
          {links.map((link) => (
            <div key={link.id} className="border-b border-gray-200 last:border-0 pb-3 md:pb-4 last:pb-0">
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-green hover:text-brand-green/80 font-semibold text-base md:text-lg hover:underline inline-flex items-center gap-1.5 transition-colors break-words"
              >
                <span className="break-all">{link.title}</span>
                <span className="text-sm md:text-base flex-shrink-0">↗</span>
              </a>
              {link.description && (
                <p className="text-xs md:text-sm text-brand-charcoal mt-1 md:mt-2 break-words">{link.description}</p>
              )}
            </div>
          ))}
        </div>
      </Card>
    )
  }

  const renderGallerySection = (section) => {
    const items = portal_gallery_items
      .filter(item => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

    if (items.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-4 text-sm">No images yet.</p>
        </Card>
      )
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {items.map(item => (
          <div key={item.id} className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
            <img src={item.image_url} alt={item.title || 'Gallery image'} className="w-full h-40 md:h-48 object-cover" />
            {(item.title || item.caption) && (
              <div className="p-3">
                {item.title && <p className="text-sm font-medium text-brand-charcoal">{item.title}</p>}
                {item.caption && <p className="text-xs text-brand-slate mt-1">{item.caption}</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderVideosSection = (section) => {
    const videos = portal_video_items
      .filter(item => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

    if (videos.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-4 text-sm">No videos available yet.</p>
        </Card>
      )
    }

    return (
      <div className="space-y-5">
        {videos.map(video => (
          <Card key={video.id} className="overflow-hidden">
            <div className="aspect-video bg-black">
              {video.embed_url ? (
                <iframe
                  src={video.embed_url}
                  title={video.title || 'Embedded video'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-white text-sm">
                  Unable to load video preview
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-heading text-lg text-brand-charcoal">{video.title || 'Untitled video'}</h3>
              {video.description && <p className="text-sm text-brand-slate mt-2">{video.description}</p>}
            </div>
          </Card>
        ))}
      </div>
    )
  }

  const renderAttachmentsSection = (section) => {
    const attachments = portal_attachment_items
      .filter(item => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

    if (attachments.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-4 text-sm">No downloads published yet.</p>
        </Card>
      )
    }

    return (
      <Card>
        <ul className="divide-y divide-border">
          {attachments.map(file => (
            <li key={file.id} className="py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <p className="font-medium text-brand-charcoal text-sm md:text-base">{file.file_name}</p>
                <p className="text-xs text-brand-slate">{formatFileSize(file.file_size)}</p>
              </div>
              <a
                href={file.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs md:text-sm bg-brand-green text-white rounded hover:bg-brand-green/90"
              >
                Download
              </a>
            </li>
          ))}
        </ul>
      </Card>
    )
  }

  const renderContactFormSection = (section) => {
    const config = section.config?.contactForm || {
      recipients: [],
      successMessage: 'Thanks for reaching out! We will get back to you soon.',
      collectPhone: true
    }

    const formState = contactForms[section.id] || {
      name: '',
      email: '',
      phone: '',
      message: '',
      status: 'idle',
      error: ''
    }

    const updateForm = (patch) => {
      setContactForms(prev => ({
        ...prev,
        [section.id]: {
          ...formState,
          ...patch
        }
      }))
    }

    const handleSubmit = async (event) => {
      event.preventDefault()

      if (!formState.name.trim() || !formState.email.trim() || !formState.message.trim()) {
        updateForm({ status: 'error', error: 'Please complete all required fields.' })
        return
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email.trim())) {
        updateForm({ status: 'error', error: 'Please enter a valid email address.' })
        return
      }

      updateForm({ status: 'submitting', error: '' })

      try {
        const { error } = await supabase.from('portal_contact_requests').insert({
          team_id: team.id,
          section_id: section.id,
          submitter_name: formState.name.trim(),
          submitter_email: formState.email.trim(),
          submitter_phone: formState.phone.trim() || null,
          message: formState.message.trim()
        })

        if (error) throw error

        updateForm({
          name: '',
          email: '',
          phone: '',
          message: '',
          status: 'success',
          error: ''
        })
      } catch (err) {
        console.error('Contact form submission failed:', err)
        updateForm({ status: 'error', error: 'We were unable to send your message. Please try again later.' })
      }
    }

    return (
      <Card>
        <form className="space-y-3" onSubmit={handleSubmit} aria-busy={formState.status === 'submitting'}>
          <div>
            <label className="block text-sm font-medium text-brand-charcoal">Your Name</label>
            <input
              type="text"
              value={formState.name}
              onChange={(e) => updateForm({ name: e.target.value, status: 'idle', error: '' })}
              placeholder="Jane Parent"
              className="mt-1 w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-brand-green"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-charcoal">Email</label>
            <input
              type="email"
              value={formState.email}
              onChange={(e) => updateForm({ email: e.target.value, status: 'idle', error: '' })}
              placeholder="you@example.com"
              className="mt-1 w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-brand-green"
            />
          </div>
          {config.collectPhone && (
            <div>
              <label className="block text-sm font-medium text-brand-charcoal">Phone (optional)</label>
              <input
                type="tel"
                value={formState.phone}
                onChange={(e) => updateForm({ phone: e.target.value, status: 'idle', error: '' })}
                placeholder="555-123-4567"
                className="mt-1 w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-brand-charcoal">Message</label>
            <textarea
              rows={4}
              value={formState.message}
              onChange={(e) => updateForm({ message: e.target.value, status: 'idle', error: '' })}
              placeholder="Let us know how we can help..."
              className="mt-1 w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-brand-green"
            />
          </div>
          {formState.error && (
            <p className="text-xs text-red-600" role="alert">{formState.error}</p>
          )}
          {formState.status === 'success' && (
            <p className="text-xs text-green-600" role="status" aria-live="polite">{config.successMessage}</p>
          )}
          <button
            type="submit"
            disabled={formState.status === 'submitting'}
            className="w-full px-4 py-2 bg-brand-green text-white rounded hover:bg-brand-green/90 disabled:opacity-60"
          >
            {formState.status === 'submitting' ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </Card>
    )
  }

  const renderEventCalendarSection = (section) => {
    const config = section.config?.eventCalendar || {
      weeksAhead: 4,
      includePastWeeks: 0,
      groupBy: 'month',
      showLocations: true
    }

    const now = new Date()
    const startWindow = new Date(now)
    startWindow.setDate(startWindow.getDate() - (config.includePastWeeks || 0) * 7)
    const endWindow = new Date(now)
    endWindow.setDate(endWindow.getDate() + (config.weeksAhead || 4) * 7)

    const events = calendar_events
      .map(event => ({ ...event, date: new Date(event.start_time) }))
      .filter(event => event.date >= startWindow && event.date <= endWindow)
      .sort((a, b) => a.date - b.date)

    if (events.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-4 text-sm">No events scheduled in this range.</p>
        </Card>
      )
    }

    const grouped = groupEvents(events, config.groupBy)

    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([label, groupedEvents]) => (
          <Card key={label}>
            <h3 className="text-sm font-semibold text-brand-charcoal mb-2">{label}</h3>
            <ul className="space-y-2">
              {groupedEvents.map(event => (
                <li key={event.id} className="border border-border rounded-lg p-3">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <p className="font-medium text-brand-charcoal text-sm md:text-base">{event.title}</p>
                      <p className="text-xs text-brand-slate">
                        {formatEventDate(event.date)} • {event.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                      {config.showLocations && event.location && (
                        <p className="text-xs text-brand-slate mt-1">📍 {event.location}</p>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-xs text-brand-slate md:w-1/2">{event.description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    )
  }

  const renderSocialFeedSection = (section) => {
    const items = portal_social_links
      .filter(item => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

    if (items.length === 0) {
      return (
        <Card>
          <p className="text-center text-brand-slate py-4 text-sm">No social updates yet.</p>
        </Card>
      )
    }

    return (
      <div className="space-y-3">
        {items.map(item => (
          <Card key={item.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded bg-purple-100 text-purple-700">
                {item.platform || 'Social'}
              </span>
              {item.display_name && <p className="text-sm font-semibold text-brand-charcoal">{item.display_name}</p>}
            </div>
            {item.profile_url && (
              <a
                href={item.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand-green hover:underline break-words"
              >
                {item.profile_url}
              </a>
            )}
            {item.embed_html && (
              <div
                className="mt-2 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: item.embed_html }}
              />
            )}
          </Card>
        ))}
      </div>
    )
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const groupEvents = (events, mode) => {
    return events.reduce((groups, event) => {
      let key = 'Schedule'
      if (mode === 'week') {
        const startOfWeek = new Date(event.date)
        const day = startOfWeek.getDay()
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1)
        startOfWeek.setDate(diff)
        key = `Week of ${startOfWeek.toLocaleDateString()}`
      } else if (mode === 'month') {
        key = event.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      } else if (mode === 'day') {
        key = event.date.toLocaleDateString()
      }

      if (!groups[key]) groups[key] = []
      groups[key].push(event)
      return groups
    }, {})
  }

  const formatEventDate = (date) => date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })

  return (
    <div className="min-h-screen bg-brand-cream py-4 md:py-8">
      <Container>
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-2 md:mb-4 text-center px-4">
            {team_name}
          </h1>
          <p className="text-center text-brand-slate mb-4 md:mb-6 text-sm md:text-base">
            Team Portal
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto px-4">
            <div className="relative">
              <svg 
                className="absolute left-6 md:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-brand-slate"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search dispatches, wiki, and events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 md:pl-12 pr-4 py-2.5 md:py-3 text-sm md:text-base border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* This Week Card - Prominent overview for parents */}
        <ThisWeekCard
          events={calendar_events}
          wikiItems={wiki_items}
          dispatches={dispatches}
          teamName={team_name}
        />

        {/* Dynamic Portal Sections */}
        {visibleSections.length === 0 ? (
          <Card className="text-center py-12">
            <p className="text-brand-slate">No sections configured for this portal yet.</p>
          </Card>
        ) : sidebarSections.length > 0 ? (
          /* Two-column layout when sidebar sections exist */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main Content - 2/3 width on desktop, full width on mobile/tablet */}
            <div className="lg:col-span-2 space-y-6 lg:space-y-8">
              {mainSections.map(section => {
                const content = renderSection(section)
                return content ? (
                  <section key={section.id} id={section.section_type === 'wiki' ? 'wiki' : undefined}>
                    <h2 className="font-heading text-xl md:text-2xl font-bold mb-3 md:mb-4 px-4 lg:px-0">{section.title}</h2>
                    <div className="px-4 lg:px-0">
                      {content}
                    </div>
                  </section>
                ) : null
              })}
            </div>
            
            {/* Sidebar - 1/3 width on desktop, full width on mobile/tablet */}
            <div className="space-y-6 lg:space-y-8">
              {sidebarSections.map(section => {
                const content = renderSection(section)
                return content ? (
                  <section key={section.id} id={section.section_type === 'wiki' ? 'wiki' : undefined}>
                    <h2 className="font-heading text-xl md:text-2xl font-bold mb-3 md:mb-4 px-4 lg:px-0">{section.title}</h2>
                    <div className="px-4 lg:px-0">
                      {content}
                    </div>
                  </section>
                ) : null
              })}
            </div>
          </div>
        ) : (
          /* Single column layout when no sidebar sections */
          <div className="max-w-4xl mx-auto space-y-6 lg:space-y-8">
            {mainSections.map(section => {
              const content = renderSection(section)
              return content ? (
                <section key={section.id} id={section.section_type === 'wiki' ? 'wiki' : undefined}>
                  <h2 className="font-heading text-xl md:text-2xl font-bold mb-3 md:mb-4 px-4 lg:px-0">{section.title}</h2>
                  <div className="px-4 lg:px-0">
                    {content}
                  </div>
                </section>
              ) : null
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center text-sm text-brand-slate">
          <p>
            Powered by{' '}
            <a href="/" className="text-brand-green hover:underline">
              Bench Bulletin
            </a>
          </p>
        </div>
      </Container>
    </div>
  )
}



export default Portal
